import React, {useState} from 'react';
import {Formik, useFormik} from 'formik';
import {
    Container,
    FilledInput,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    Radio,
    RadioGroup,
    SvgIcon,
    Typography,
} from "@mui/material";
import {makeStyles} from "@mui/styles";
import PrimaryButton from "../../Common/PrimaryButton";
import {ReactComponent as Important} from "../../../../svg/LightBlue/Important.svg";
import {lightBlue} from "@mui/material/colors";
import PassportAndContactForm from "./PassportAndContactForm";
import {useDispatch, useSelector} from "react-redux";
import {isDealerSite, parseDate, parseTime, requestApi, showAlertAutoDis} from "../../../utils/utils";
import {useIsAuthenticated} from "react-auth-kit";
import {useHistory, useParams} from "react-router-dom";
import {format, isPast, endOfDay, parse, differenceInDays} from "date-fns";
import {updateBookingFormData} from "../../../redux/actions/bookingFormActions";

const useStyles = makeStyles((theme) => ({
    infoContainer: {
        backgroundColor: theme.palette.lightBlue.main,
        paddingBottom: '20px',
    },
}));

const InformationForm = () => {
    const classes = useStyles();
    const [formFor, setFormFor] = useState(sessionStorage.getItem('formFor') || 'myself');
    const isAuthenticated = useIsAuthenticated();
    const {id} = useParams();
    const history = useHistory();
    const dispatch = useDispatch();

    const {form, booking} = useSelector(state => state.bookingForm);

    const boardDate = parse(booking.trip.boarddate, 'yyyy-MM-dd', new Date());
    const daysBeforeBoardDate = differenceInDays(boardDate, new Date());
    // const disableInputs = daysBeforeBoardDate < 8;
    const disableInputs = false ;


    const initialValues = {
        agreement: !!(form.guestInfo?.birthdate),
        firstName: form.guestInfo?.firstName || '',
        middleName: form.guestInfo?.middleName || '',
        lastName: form.guestInfo?.lastName || '',
        sex: form.guestInfo?.sex || '',
        beds: form.guestInfo?.beds || '',
        birthDate: parseDate(form.guestInfo?.birthdate),
        passportNum: form.passportNum || '',
        passportNumVerification: form.passportNum || '',
        citizenIso: form.citizenIso || '',
        passportDateOfIssue: parseDate(form.passportDateOfIssue),
        passportExpiryDate: parseDate(form.passportExpiryDate),
        passportPlaceOfIssue: form.passportPlaceOfIssue || '',
        passportPlaceOfBirth: form.passportPlaceOfBirth || '',
        email: form.guestInfo?.email || '',
        phone: form.guestInfo?.phone || '',
        address: form.address || '',
        address2: form.address2 || '',
        city: form.city || '',
        state: form.state || '',
        postcode: form.postcode || '',
        countryIso: form.countryIso || '',
        flightInHotel: form.flightInHotel || '',
        flightInDate: parseDate(form.flightInDate),
        flightInTime: parseTime(form.flightInTime),
        flightInAirline: form.flightInAirline || '',
        flightOutDate: parseDate(form.flightOutDate),
        flightOutTime: parseTime(form.flightOutTime),
        flightOutAirline: form.flightOutAirline || '',
        meetIn: form.meetIn || '',

    };

    const codeForm = useFormik({
        initialValues: {
            friendFirstName: '',
            friendLastName: '',
            friendEmail: '',
        },
        onSubmit: values => handleCodeGeneration(values)
    });

    const handleSubmit = async (values) => {
        // Validations
        if (values.passportNum !== values.passportNumVerification)
            return showAlertAutoDis({
                title: 'Error',
                text: 'Passport Number and Confirm Passport Number fields must match.'
            });

        if (values.agreement)
            sessionStorage.setItem('agreement', values.agreement);
        else
            return showAlertAutoDis({
                title: 'Error',
                text: 'You must accept the terms and conditions.'
            });

        if (isPast(endOfDay(values['passportExpiryDate'])))
            return showAlertAutoDis({title: 'Error', text: 'Your passport expiry date must be a future date.'});

        try {
            const params = {...values};
            params['birthdate'] = values['birthDate'] ? format(values['birthDate'], 'yyyy-MM-dd') : null;
            params['passportDateOfIssue'] = values['passportDateOfIssue'] ? format(values['passportDateOfIssue'], 'yyyy-MM-dd') : null;
            params['passportExpiryDate'] = values['passportExpiryDate'] ? format(values['passportExpiryDate'], 'yyyy-MM-dd') : null;

            const access = sessionStorage.getItem('accessToken');
            if (access) params['guestCode'] = access;

            dispatch(updateBookingFormData(params, id));

            return history.push(`/booking/guest/${id}/medical-insurance`);

        } catch (e) {
            // console.log(e);
        }
    };

    const handleCodeGeneration = async (values) => {
        try {
            const params = {
                guestId: id,
                email: values.friendEmail,
                firstName: values.friendFirstName,
                lastName: values.friendLastName,
            };
            const {data} = await requestApi('/guest/create-guestcode', 'POST', params);
            if (data.status === 'success' && data.sent) {
                sessionStorage.removeItem('formFor');
                return showAlertAutoDis({
                    title: 'Code Generated',
                    text: 'An email with the access code has been sent successfully.'
                });
            }
            throw new Error();
        } catch (e) {
            showAlertAutoDis({title: 'Error', text: 'Error generating guest code'});
        }
    };

    const handleSetFormFor = (value) => {
        sessionStorage.setItem('formFor', value);
        setFormFor(value);
    };

    return (
        <>
            {isAuthenticated() && <Container>
                <Grid container style={{marginBottom: '50px'}} sx={{px: {xs: '1.6rem', lg: '0'}}}>
                    <Typography variant="h3" style={{fontSize: '1.6rem', fontWeight: '600'}}>
                        <span style={{color: 'red'}}>*</span>Please select one from:
                    </Typography>
                    <Grid item xs={12} md={12} style={{marginTop: '10px'}}>
                        <FormControl component="fieldset">
                            <RadioGroup aria-label="info" name="formFor" value={formFor}
                                        onChange={(e) => handleSetFormFor(e.target.value)}>
                                <FormControlLabel value="myself" control={<Radio/>}
                                                  label="I’m filling this information form for myself."/>
                                <FormControlLabel value="friend" control={<Radio/>}
                                                  label={`I’m filling this information form for ${isDealerSite() ? 'a guest' : 'my friend'} who is travelling with me.`}/>
                                <FormControlLabel value="code" control={<Radio/>}
                                                  label={`I’d like to send this form to ${isDealerSite() ? 'a guest' : 'my friend'} to fill out.`}/>
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    {formFor === 'code' && (
                        <Grid container spacing={2} style={{marginTop: '0'}} justifyContent="center">
                            <Grid item xs={12} md={8}>
                                <Typography component="span" style={{fontSize: '1rem'}}>
                                    *Please provide their name and email address and click the “send” button to get
                                    them
                                    started.
                                    They will get an email with a secure, unique access code
                                    to put in the details needed.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={10}>
                                <form onSubmit={codeForm.handleSubmit}>
                                    <Grid container spacing={2} justifyContent="center">
                                        <Grid item xs={12} md={3}>
                                            <FormControl variant="filled" fullWidth>
                                                <InputLabel htmlFor="friendFirstName">First Name</InputLabel>
                                                <FilledInput id="friendFirstName" name="friendFirstName"
                                                             onChange={codeForm.handleChange}
                                                             value={codeForm.values.friendFirstName}
                                                             disableUnderline/>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <FormControl variant="filled" fullWidth>
                                                <InputLabel htmlFor="friendLastName">Last Name</InputLabel>
                                                <FilledInput id="friendLastName" name="friendLastName"
                                                             onChange={codeForm.handleChange}
                                                             value={codeForm.values.friendLastName}
                                                             disableUnderline/>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <FormControl variant="filled" fullWidth>
                                                <InputLabel htmlFor="friendEmail">Email</InputLabel>
                                                <FilledInput id="friendEmail" name="friendEmail"
                                                             onChange={codeForm.handleChange}
                                                             value={codeForm.values.friendEmail}
                                                             disableUnderline/>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <PrimaryButton style={{height: '100%'}} sx={{margin: {xs: '0 auto', md: 0}}}
                                                           type="submit">Send</PrimaryButton>
                                        </Grid>

                                    </Grid>
                                </form>
                            </Grid>
                            <Grid item xs={12} style={{marginTop: 20}}>
                                <Grid item container justifyContent="center" className={classes.infoContainer}>
                                    <SvgIcon component={Important} viewBox="0 0 50 50" sx={{
                                        stroke: lightBlue[500],
                                        fill: lightBlue[500],
                                        width: '50px',
                                        height: '50px',
                                        margin: '0 auto'
                                    }}/>
                                    <Grid container justifyContent={"center"}>
                                        <Grid item xs={12} md={8}>
                                            <Typography variant="h3" style={{
                                                fontSize: '1.4rem',
                                                fontWeight: '600',
                                                textAlign: 'center'
                                            }}>
                                                Personal and Passport information are used to generate Customs
                                                Manifests
                                                for Mexican authorities.
                                                <br/>
                                                Please ensure the information provided here is accurate.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>)}
                </Grid>
            </Container>}
            {disableInputs && <Typography variant="p" style={{fontSize: '1.6rem'}}>
                <span style={{fontWeight: '600'}}>Form fields disabled. It is less than a week until your trip.</span><br/>
                Guest Information must be entered more than 1 week prior to your trip through this form.<br/>
                Please contact our office if you have any changes to the information on this page at +1-604-241-1918 or
                &nbsp;<a href="mailto:info@nautilusdiveadventures.com">info@nautilusdiveadventures.com</a>
            </Typography>}
            <Formik initialValues={initialValues} onSubmit={(values) => handleSubmit(values)}>
                {(props) => <PassportAndContactForm {...props} />}
            </Formik>

        </>
    );
};

export default InformationForm;